import { ChevronDown, X } from "lucide-react";
import { useEffect, useState } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import Link from "next/link";
import Image from "next/image";
import { menuItems } from "../utils/consts";
import { MenuItem } from "../utils/types";
import { NavLink } from "./nav-link";
import Logo from "@/components/shared/Logo";
import FancyButton from "@/components/ui/fancy-button";
import { cn } from "@/lib/utils";

export const MobileStickyNavbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const toggleMenu = () => setIsOpen(!isOpen);

  useEffect(() => {
    const preventScroll = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
      return false;
    };

    if (isOpen) {
      document.addEventListener("touchmove", preventScroll, { passive: false });
      document.addEventListener("wheel", preventScroll, { passive: false });
    }
    return () => {
      document.removeEventListener("touchmove", preventScroll);
      document.removeEventListener("wheel", preventScroll);
    };
  }, [isOpen]);

  return (
    <div className="sticky top-[1vh] z-50 flex w-full justify-center md:hidden">
      {/* added backdrop blur but feel free to remove */}
      <div className="relative flex w-full max-w-[90%] items-center justify-between rounded-xl border bg-white/60 px-4 py-5 backdrop-blur-2xl">
        <Logo
          className="fixed left-4 top-[1.1rem] h-5 w-auto"
          href="/"
          onClick={(e) => {
            e.stopPropagation();
            if (window.location.pathname === "/") {
              e.preventDefault();
            }
            toggleMenu();
          }}
        />
        <Accordion
          type="single"
          collapsible
          orientation="horizontal"
          value={isOpen ? "mobile-menu" : ""}
          onValueChange={toggleMenu}
          className="w-full !p-0 md:hidden"
        >
          <AccordionItem value="mobile-menu" className="border-none pb-0">
            <AccordionTrigger value="mobile-menu" className="flex w-full justify-end p-0">
              <div className="-mt-1 size-4 transition-transform duration-200 hover:scale-110 ">
                <Image
                  src="https://cdn-crayo.com/lp/public/hamburger.png"
                  alt="Menu"
                  width={16}
                  height={16}
                  className="group-data-[state=open]:hidden"
                  draggable={false}
                  style={{
                    userSelect: "none",
                    pointerEvents: "none",
                    touchAction: "none",
                  }}
                />
                <X className="size-6 -translate-x-1 -translate-y-[5px] group-data-[state=closed]:hidden" />
              </div>
            </AccordionTrigger>
            <AccordionContent className="flex h-fit flex-col gap-6 pb-0 group-data-[state=open]:overflow-hidden">
              {/* mobile menu */}
              <div className="flex h-fit flex-col pt-4">
                <Accordion type="single" collapsible>
                  <AccordionItem value="features">
                    <AccordionTrigger className="align-start flex flex-1 items-center justify-between gap-2 py-2 pb-2 pt-3 text-base font-medium transition-all [&[data-state=open]>svg]:rotate-180">
                      Features
                      <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200" />
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-3">
                        <div>
                          <p className="mb-2 border-b pb-1 text-[10px]">GENERATE VIDEOS</p>
                          {menuItems.features.generateVideos.map((item: MenuItem) => (
                            <NavLink key={item.title} {...item} onClick={toggleMenu} />
                          ))}
                        </div>
                        <div>
                          <p className="mb-2 border-b pb-1 text-[10px]">CRAYO TOOLS</p>
                          {menuItems.features.tools.map((item) => (
                            <NavLink key={item.title} {...item} onClick={toggleMenu} />
                          ))}
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="resources">
                    <AccordionTrigger className="align-start flex flex-1 items-center justify-between gap-2 py-2 pb-2 pt-3 text-base font-medium transition-all [&[data-state=open]>svg]:rotate-180">
                      Resources
                      <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200" />
                    </AccordionTrigger>
                    <AccordionContent>
                      <ul className="grid gap-1">
                        {menuItems.resources.map((item) => (
                          <NavLink key={item.title} {...item} onClick={toggleMenu} />
                        ))}
                      </ul>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>

                <Link
                  prefetch={false}
                  href="https://crayo.ai/discord"
                  className="align-center block items-center justify-center border-b pb-3 pt-4 text-start text-base font-medium tracking-tight"
                  onClick={toggleMenu}
                >
                  Blog
                </Link>

                <Link
                  href="/pricing"
                  className="align-center block items-center justify-center border-b pb-3 pt-4 text-start text-base font-medium tracking-tight"
                  onClick={toggleMenu}
                >
                  Pricing
                </Link>
                <div className="flex w-full items-center justify-center p-1">
                  <FancyButton
                    href="https://crayo.ai/dashboard"
                    className="mt-4 block !w-full items-center justify-center text-center"
                    onClick={toggleMenu}
                  >
                    Sign Up
                  </FancyButton>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    </div>
  );
};
